'use client';
import { useState } from 'react';
import {
    BadgePercent,
    CalendarDaysIcon,
    ChevronLeft,
    ChevronRight,
    Mail,
    MapPinned,
    SmartphoneIcon,
    UsersRound,
} from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

import { Button } from '@/components/ui/button';
import { CarouselApi, CarouselContent, CarouselItem } from '@/components/ui/carousel';
import { Carousel } from '@/components/ui/carousel';
import { Link } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { Event } from '@/types/event';

import { BackButton } from '../../recipes/[id]/components/back-button';
import { ShareButton } from '../../recipes/[id]/components/share_button';
import EventCard from '../components/event-card';

// Facebook и Instagram иконки
const FacebookIcon = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={className} {...props}>
        <Image src='/svg/facebook.svg' alt='Facebook' width={24} height={24} />
    </div>
);

const InstagramIcon = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
    <div className={className} {...props}>
        <Image src='/svg/instagram.svg' alt='Instagram' width={24} height={24} />
    </div>
);

interface EventPageClientProps {
    event: Event;
    similarEvents: Event[];
}

export default function EventPageClient({ event, similarEvents }: EventPageClientProps) {
    const t = useTranslations();
    const [api, setApi] = useState<CarouselApi>();
    // console.log(event);

    const aboutEventInfo = [
        {
            id: 1,
            icon: CalendarDaysIcon,
            text: event.datetime,
        },
        {
            id: 2,
            icon: MapPinned,
            text: event.location,
        },
        {
            id: 3,
            icon: BadgePercent,
            text: event.payment_type,
        },
        {
            id: 4,
            icon: UsersRound,
            text: event.type.title,
        },
    ];
    const aboutOrganizerInfo = [
        {
            id: 1,
            icon: SmartphoneIcon,
            text: event.phone,
            type: 'phone',
        },
        {
            id: 2,
            icon: Mail,
            text: event.email,
            type: 'email',
        },
        {
            id: 3,
            icon: BadgePercent,
            text: event.link,
            type: 'link',
        },
        {
            id: 4,
            icon: FacebookIcon,
            text: 'facebook', // event.facebook ||
            type: 'facebook',
        },
        {
            id: 5,
            icon: InstagramIcon,
            text: 'instagram', // event.instagram ||
            type: 'instagram',
        },
    ];
    return (
        <div className='mx-4 mb-6 flex max-w-[1200px] flex-col pt-9 md:mx-auto md:mb-16'>
            <div className='relative'>
                <div className='text-purple-lavender flex items-center justify-between pb-6'>
                    <BackButton />
                    <ShareButton />
                </div>
            </div>
            <div className='mb-2 flex items-center justify-between md:mb-6'>
                <p className='text-purple lg:font-gilroy text-xl font-semibold leading-[100%] lg:align-middle lg:text-[44px] lg:font-extrabold lg:leading-[100%] lg:tracking-normal'>
                    {event.title}
                </p>
                {event.is_registration_enabled && event.form_builder?.length > 0 ? (
                    <Link href={`/events/${event.id}/register`} target={event.register_link ? '_blank' : undefined}>
                        <Button
                            variant='outline'
                            size='xl'
                            className='bg-red-coral hover:bg-red-coral  font-gilroy tracking-0 hidden px-4 py-[14px] text-center align-middle text-[16px] font-extrabold leading-[100%] text-white hover:text-white md:block'
                        >
                            {t('register-now')}
                        </Button>
                    </Link>
                ) : null}
            </div>
            <div className='relative mb-4 h-[230px] w-full md:mb-9 md:h-[526px]'>
                <Image src={event.image} alt={event.title} fill className='object-cover' />
            </div>
            <Link
                href={event.register_link ? event.register_link : `/events/${event.id}/register`}
                target={event.register_link ? '_blank' : undefined}
            >
                <Button
                    variant='outline'
                    size='xl'
                    className='bg-red-coral hover:bg-red-coral font-gilroy tracking-0 mb-6 px-4 py-[14px] text-center align-middle text-[16px] font-extrabold leading-[100%] text-white hover:text-white md:hidden'
                >
                    {t('register-now')}
                </Button>
            </Link>
            {/* About event */}
            <div className='mb-6 flex flex-col gap-4'>
                <p className='text-purple font-gilroy align-middle text-[20px] font-extrabold leading-[100%] tracking-normal md:text-[36px] md:tracking-[-0.012em]'>
                    {t('about-event')}
                </p>
                <div
                    className='text-purple-deep font-gilroy prose max-w-none text-base font-light leading-[100%] tracking-[0.36px] [&_img]:mx-auto [&_p]:text-center'
                    dangerouslySetInnerHTML={{ __html: event.about_event }}
                />
                <p className='text-red-coral font-gilroy align-middle text-base font-medium leading-[22px] tracking-normal'>
                    {t('deadline')} : {event.expires_at}
                </p>
            </div>
            <div className='bg-purple-light mb-6 grid grid-cols-1 gap-4 p-4 text-white md:m-0 md:mb-9 md:grid-cols-2 lg:grid-cols-4 lg:gap-[24px]'>
                {aboutEventInfo.map(info => {
                    const InfoIcon = info.icon;
                    return (
                        <div key={info.id} className='flex items-center justify-center gap-2 lg:pb-[18px] lg:pt-[15px]'>
                            <InfoIcon className='size-6 min-w-6' />
                            <p className='font-gilroy overflow-hidden break-words text-base font-medium leading-[22px] tracking-normal'>
                                {info.text}
                            </p>
                        </div>
                    );
                })}
            </div>
            {/* About the organizer */}
            <div className=' flex flex-col gap-4'>
                <p className='text-purple font-gilroy align-middle text-[20px] font-extrabold leading-[100%] tracking-normal md:text-[36px] md:tracking-[-0.012em]'>
                    {t('about-organizer')}
                </p>
                <div
                    className='text-purple-deep font-gilroy prose max-w-none text-base font-light leading-[100%] tracking-[0.36px] [&_img]:mx-auto [&_p]:text-center'
                    dangerouslySetInnerHTML={{ __html: event.about_organizer }}
                />
                <div className='bg-purple-light mb-9 grid grid-cols-1 gap-4 p-4 text-white md:m-0 md:mb-9 md:grid-cols-2 lg:grid-cols-5 lg:gap-[24px]'>
                    {aboutOrganizerInfo.map(info => {
                        const InfoIcon = info.icon;

                        return (
                            <div
                                key={info.id}
                                onClick={() => {
                                    if (info.type === 'link' || info.type === 'facebook' || info.type === 'instagram') {
                                        window.open(info.text, '_blank');
                                    }
                                    if (info.type === 'phone') {
                                        window.open(`tel:${info.text}`, '_blank');
                                    }
                                    if (info.type === 'email') {
                                        window.open(`mailto:${info.text}`, '_blank');
                                    }
                                }}
                                className={cn(
                                    'flex cursor-pointer items-center gap-2 md:p-0 lg:pb-[18px] lg:pt-[15px] justify-center',
                                )}
                            >
                                <InfoIcon className='size-6 min-w-6' />
                                <p className='font-gilroy overflow-hidden break-words text-base font-medium leading-[22px] tracking-normal'>
                                    {info.text}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Similar events */}
            {similarEvents?.length > 0 && (
                <div className=' flex flex-col gap-4 md:gap-6'>
                    <p className='text-purple font-gilroy align-middle text-[20px] font-extrabold leading-[100%] tracking-normal md:text-[36px] md:tracking-[-0.012em]'>
                        {t('similar-events')}
                    </p>
                    <div className='relative'>
                        <Carousel setApi={setApi}>
                            <CarouselContent className='gap-4 md:gap-6 '>
                                {similarEvents.map(event => (
                                    <CarouselItem key={event.id} className='min-[300px] min-h-[284px] !basis-auto'>
                                        <Link href={`/events/${event.id}`}>
                                            <EventCard isCarousel event={event} />
                                        </Link>
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                        </Carousel>
                        {similarEvents?.length > 3 && (
                            <div className='pointer-events-none absolute inset-x-0 top-1/2 flex justify-between '>
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    onClick={() => api?.scrollPrev()}
                                    className='text-purple-royal pointer-events-auto absolute left-[-40px] z-10 items-center justify-center [&_svg]:size-8'
                                >
                                    <ChevronLeft />
                                </Button>
                                <Button
                                    variant='ghost'
                                    size='icon'
                                    onClick={() => api?.scrollNext()}
                                    className='text-purple-royal pointer-events-auto absolute right-[-40px] z-10 items-center justify-center [&_svg]:size-8'
                                >
                                    <ChevronRight />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
